import { useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { middleTruncate } from '../items/PLDocumentList';

const optionLabelSx = { alignItems: 'flex-start', mx: 0, '& .MuiRadio-root': { p: 0.5, mt: '-2px' } };

/** 교체 후보 한 건의 설명 줄. 파일명(수기면 Manual entry)·라인 수·확정 일자. */
const candidateCaption = (c) => [c.filename ? middleTruncate(c.filename) : 'Manual entry', `${c.itemCount} lines`, c.confirmedAt ? `confirmed ${String(c.confirmedAt).slice(0, 10)}` : null].filter(Boolean).join(' · ');

/**
 * 같은 House에 같은 Invoice 확정본이 있어 확정이 멈췄을 때의 선택창. 후보는 서버가 준 확정본들이다.
 * Create new는 기존 확정본을 그대로 두고 이 문서를 별도 PL로 확정한다. Replace는 고른 확정본의 확정 라인을 이 문서로 바꾼다 — 고르면 기존 데이터가 바뀐다고 알린다.
 * Cancel은 아무것도 확정하지 않고 검수 화면으로 돌아간다. 후보가 하나면 교체 대상은 그것으로 정해 둔다.
 * notice는 고른 교체 대상이 그 사이 바뀌거나 지워져 다시 고르라는 서버 안내다. 남은 후보가 없으면 교체할 것이 없으므로 Create new만 고를 수 있다.
 * candidates가 null이면 닫혀 있다. 확정 요청이 진행 중(saving)이면 닫거나 다시 누를 수 없다.
 */
export default function DuplicateInvoiceDialog({ candidates, notice = null, invoiceNo, saving, onCancel, onChoose }) {
    const [action, setAction] = useState('create_new');
    const [targetId, setTargetId] = useState(null);

    useEffect(() => {
        if (!candidates) return;
        setAction('create_new');
        setTargetId(candidates.length === 1 ? candidates[0].id : null);
    }, [candidates]);

    // 확정이 끝나 후보가 비워져도 닫히는 동안 그려지므로 빈 목록으로 본다.
    const options = candidates ?? [];
    const noneLeft = candidates?.length === 0;
    const target = options.find((c) => c.id === targetId) ?? null;
    const replacing = action === 'replace';
    const canConfirm = !saving && (!replacing || target != null);

    return (
        <Dialog open={Boolean(candidates)} onClose={() => !saving && onCancel()} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontSize: 14, color: '#003a52' }}>{noneLeft ? 'The packing list to replace is no longer available' : `Invoice ${invoiceNo || ''} is already confirmed on this HBL`}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {notice && <Alert severity="info" sx={{ py: 0, fontSize: 12 }}>{notice}</Alert>}
                <Typography sx={{ fontSize: 12 }}>Choose how to confirm this packing list.</Typography>
                <RadioGroup value={action} onChange={(e) => setAction(e.target.value)} sx={{ gap: 1 }}>
                    <FormControlLabel
                        value="create_new"
                        control={<Radio size="small" />}
                        disabled={saving}
                        sx={optionLabelSx}
                        label={
                            <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#003a52' }}>Create new</Typography>
                                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Keep the confirmed packing list and add this one as a separate packing list with the same invoice no.</Typography>
                            </Box>
                        }
                    />
                    <FormControlLabel
                        value="replace"
                        control={<Radio size="small" />}
                        disabled={saving || noneLeft}
                        sx={optionLabelSx}
                        label={
                            <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#003a52' }}>Replace</Typography>
                                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Replace the confirmed lines of an existing packing list with this one.</Typography>
                            </Box>
                        }
                    />
                </RadioGroup>
                {replacing && (
                    <>
                        {options.length > 1 && (
                            <Box>
                                <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5 }}>Packing list to replace</Typography>
                                <RadioGroup value={targetId ?? ''} onChange={(e) => setTargetId(Number(e.target.value))} sx={{ gap: 0.5, pl: 1 }}>
                                    {options.map((c) => (
                                        <FormControlLabel
                                            key={c.id}
                                            value={c.id}
                                            control={<Radio size="small" />}
                                            disabled={saving}
                                            sx={optionLabelSx}
                                            label={
                                                <Box>
                                                    <Typography sx={{ fontSize: 12, fontWeight: 500 }}>{c.invoiceNo || invoiceNo}</Typography>
                                                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{candidateCaption(c)}</Typography>
                                                </Box>
                                            }
                                        />
                                    ))}
                                </RadioGroup>
                            </Box>
                        )}
                        {options.length === 1 && <Typography sx={{ fontSize: 11, color: 'text.secondary', pl: 1 }}>{candidateCaption(options[0])}</Typography>}
                        <Alert severity="warning" sx={{ py: 0, fontSize: 12 }}>
                            {target
                                ? `The confirmed lines of ${target.invoiceNo || invoiceNo || 'the selected packing list'} will be replaced with the lines in this document. Its items and container allocations will change.`
                                : 'Choose the packing list to replace. Its confirmed lines, items and container allocations will change.'}
                        </Alert>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button size="small" onClick={onCancel} disabled={saving} sx={{ fontSize: 12 }}>Cancel</Button>
                <Button size="small" variant="contained" onClick={() => onChoose(replacing ? { action: 'replace', target } : { action: 'create_new' })} disabled={!canConfirm} sx={{ fontSize: 12 }}>
                    {saving ? 'Confirming…' : replacing ? 'Replace' : 'Create new'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
